import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

// D10: shown after a request of this page (read or write) was refused (409 workspace_changed) because the current
// workspace changed in another tab or on another device. Nothing was written or loaded from the current workspace. It
// appears with the first such refusal: there is no guaranteed delay (a pending or failed request refuses nothing). The
// page keeps what it showed and its drafts until the user chooses: make the page's workspace current again (its reads
// are then read again in place), or reload to show the current one (as the workspace switcher does).
export default function WorkspaceChangedBanner() {
  const { workspaceChange, returnToPageWorkspace, showCurrentWorkspace } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!workspaceChange) return null;
  const back = async () => {
    setBusy(true); setError(null);
    try { await returnToPageWorkspace(); } catch (e) { setError(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <div role="alert" className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm space-y-2" data-testid="workspace-changed-banner">
      <p className="flex gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
        <span>
          <b>L’espace actif a changé dans un autre onglet ou sur un autre appareil.</b> Cette page reste sur l’espace qu’elle
          affichait : le serveur a refusé la dernière demande, sans rien enregistrer ni charger depuis l’espace actif.
          Les contenus déjà affichés et vos brouillons restent en place ; ils peuvent ne plus être à jour.
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={back} disabled={busy} data-testid="workspace-changed-return">Revenir à l’espace de cette page</Button>
        <Button type="button" size="sm" variant="outline" onClick={showCurrentWorkspace} disabled={busy} data-testid="workspace-changed-show">
          Afficher l’espace actif (recharge la page, brouillons non enregistrés perdus)
        </Button>
      </div>
      {error && <p className="text-destructive" data-testid="workspace-changed-error">{error}</p>}
    </div>
  );
}
